import type { VoidFunctionComponent } from 'react';

import { plural, pluralizeIsAre } from '../../../../shared/helpers';

import styles from './BulkSelectionBanner.module.less';

type Props = {
  /**
   * This is the singluar noun for the items being selected.
   * ie: "issue", "project", etc.
   */
  itemsNoun: string;
  /**
   * This is whatever the current pagination size is.
   */
  pageSize: number;
  /**
   * This is the total number of items across all pages.
   */
  totalItemsCount: number;
  /**
   * If all items across all pages are selected (ie: usually represented with "all").
   */
  areAllItemsSelected: boolean;
  /**
   * This text is displayed in the "select all" button.
   * ie: "Select all 11 issues across in {sectionName}"
   */
  sectionName: string;
  /**
   * Callback for selecting all items across all pages.
   */
  onSelectAll: () => void;
  /**
   * Callback for clearing the selection.
   */
  onClearSelection: () => void;
};

/**
 * BulkSelectionBanner is a component which is displayed on large, paginated lists
 * of items which allow the user to select multiple items at once via checkboxes.
 *
 * This banner is displayed when all items on the current page are selected,
 * but not all items across all pages.
 *
 * It provides a button to either select all items across all pages, or clear the selection.
 */
const BulkSelectionBanner: VoidFunctionComponent<Props> = ({
  itemsNoun,
  pageSize,
  totalItemsCount,
  areAllItemsSelected,
  sectionName,
  onSelectAll,
  onClearSelection,
}) => {
  const totalPluralizedNoun = plural(totalItemsCount, itemsNoun);
  const areIs = pluralizeIsAre(totalItemsCount);
  // in cases where the section and the noun match we want to display different text
  const isCategorySameAsNoun: boolean = plural(2, itemsNoun).toLowerCase() === sectionName.toLowerCase();

  if (areAllItemsSelected) {
    return (
      <div className={styles.container}>
        <div className={`animated quick fadeInDown fadeOutUp ${styles.banner}`}>
          <span>
            All {totalItemsCount.toLocaleString()} {totalPluralizedNoun} {isCategorySameAsNoun ? '' : `in ${sectionName} `}{areIs} selected.
          </span>
          {' '}
          <button
            type="button"
            className={styles.actionButton}
            onClick={onClearSelection}
          >
            Clear selection.
          </button>
        </div>
      </div>
    );
  }

  const currentPagePluralizedNoun = plural(pageSize, itemsNoun);
  return (
    <div className={styles.container}>
      <div className={`animated quick fadeInDown fadeOutUp ${styles.banner}`}>
        <span>
          All {pageSize.toLocaleString()} {currentPagePluralizedNoun} on this page {areIs} selected.
        </span>
        {' '}
        <button
          type="button"
          className={styles.actionButton}
          onClick={onSelectAll}
        >
          Select all {totalItemsCount.toLocaleString()} {totalPluralizedNoun} in {sectionName}.
        </button>
      </div>
    </div>
  );
};

export default BulkSelectionBanner;
